'use client';
import type { CSSProperties, ReactNode } from 'react';
import { useViewport } from '@xyflow/react';
/**
 * GridPatternBackground — the viewport-locked dotted grid + radial fade that
 * sits behind every graph widget (mindmap, flowchart). Moved verbatim from the
 * mindmap so both widgets share one source.
 *
 * It reads the live react-flow viewport (so the grid pans/zooms with the
 * canvas) and must be rendered as a child of `<ReactFlow>`.
 */
/** Base grid cell size in px at zoom 1. */
const GRID_PATTERN_BASE_GAP = 32;
const GRID_PATTERN_SVG = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${GRID_PATTERN_BASE_GAP}" height="${GRID_PATTERN_BASE_GAP}" viewBox="0 0 ${GRID_PATTERN_BASE_GAP} ${GRID_PATTERN_BASE_GAP}"><path d="M ${GRID_PATTERN_BASE_GAP} 0 L 0 0 0 ${GRID_PATTERN_BASE_GAP}" fill="none" stroke="rgb(147, 216, 255)" stroke-opacity="0.18" stroke-width="1"/></svg>`);
/** The grid as a repeatable CSS `background-image`. */
const GRID_PATTERN_IMAGE = `url("data:image/svg+xml,${GRID_PATTERN_SVG}")`;
/** Radial fade so the grid dissolves toward the canvas edges. */
const GRID_FADE_MASK = 'radial-gradient(ellipse at center, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0) 75%)';
export interface GridPatternBackgroundProps {
    /** Extra styles merged over the base (e.g. to override the fade). */
    style?: CSSProperties;
    /** Stacking order within the canvas. Defaults to 0 (behind nodes/edges). */
    zIndex?: number;
}
export function GridPatternBackground({ style, zIndex = 0, }: GridPatternBackgroundProps): ReactNode {
    const viewport = useViewport();
    const scaledGap = Math.max(1, GRID_PATTERN_BASE_GAP * viewport.zoom);
    const offsetX = ((viewport.x % scaledGap) + scaledGap) % scaledGap;
    const offsetY = ((viewport.y % scaledGap) + scaledGap) % scaledGap;
    return (
      <div style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex,
            backgroundImage: GRID_PATTERN_IMAGE,
            backgroundRepeat: 'repeat',
            backgroundSize: `${scaledGap}px ${scaledGap}px`,
            backgroundPosition: `${offsetX}px ${offsetY}px`,
            maskImage: GRID_FADE_MASK,
            WebkitMaskImage: GRID_FADE_MASK,
            ...style,
        }}/>
    );
}
